// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import Skeleton from "@eisinc/App/Components/Skeleton/Skeleton";
import XngCarousel, { SwiperStyle } from "@eisinc/App/Components/XngCarousel";
import { CustomAppState } from "@eisinc/App/Store/Reducers";
import { VideoDocumentModel } from "@eisinc/App/Store/Reducers/ProductDetails/state";
import { getProductVideos } from "@eisinc/App/Store/Selectors/ProductDetailsSelectors";
import { XProductContextModel } from "@eisinc/Overrides/Pages/ProductDetailsPage";
import VideoPlayButton from "@eisinc/XDL/Icons/VideoPlayButton";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import translate from "@insite/client-framework/Translate";
import { ImageModel } from "@insite/client-framework/Types/ApiModels";
import { ButtonProps } from "@insite/mobius/Button";
import Clickable from "@insite/mobius/Clickable";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import { IconPresentationProps } from "@insite/mobius/Icon";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import { injectCss } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import styled, { css } from "styled-components";

interface OwnProps {
    numberOfVisibleThumbnails?: number;
    selectedIndex?: number;
    onSelectImage?: (index: number) => void;
    extendedStyles?: ProductImageCarouselStyles;
    // XNG-Changes
    selectedVideoIndex?: number;
    onSelectVideo?: (index: number) => void;
    scrollingBehavior?: "horizontal" | "vertical";
}

const mapStateToProps = (state: CustomAppState, ownProps: HasProductContext) => ({
    productSettings: getSettingsCollection(state).productSettings,
    // XNG-Change
    videos: getProductVideos(state, ownProps.productContext?.product),
    isTabletScreen: state.xCommon.isTabletScreen,
});

type Props = OwnProps & HasProductContext & ReturnType<typeof mapStateToProps>;

export interface ProductImageCarouselStyles {
    mainContainer?: GridContainerProps;
    prevArrowGridItem?: GridItemProps;
    prevArrowButton?: ButtonProps;
    carouselGridItem?: GridItemProps;
    carouselWrapper?: InjectableCss;
    carouselContainer?: InjectableCss;
    carouselSlidesContainer?: InjectableCss;
    carouselSlide?: InjectableCss;
    carouselSlideInner?: InjectableCss;
    image?: LazyImageProps;
    selectedImage?: LazyImageProps;
    nextArrowGridItem?: GridItemProps;
    videoIcon?: IconPresentationProps;
    nextArrowButton?: ButtonProps;
    verticalPrevArrowGridItem?: GridItemProps;
    verticalCarouselGridItem?: GridItemProps;
    verticalNextArrowGridItem?: GridItemProps;
    // XNG-Change
    carousel?: SwiperStyle;
    loadingWrapper?: InjectableCss;
}

const getImageCss = (borderColor: string) => css`
    width: 92px;
    height: 92px;
    border: 1px solid ${getColor(borderColor)};
    cursor: pointer;
    justify-content: flex-start;
`;

export const productImageCarouselStyles: ProductImageCarouselStyles = {
    mainContainer: { gap: 0 },
    prevArrowGridItem: {
        width: 1,
        align: "middle",
        css: css`
            justify-content: flex-end;
        `,
    },
    verticalPrevArrowGridItem: {
        width: 12,
        align: "middle",
        css: css`
            justify-content: center;
        `,
    },
    prevArrowButton: {
        variant: "secondary",
        color: "text.main",
        css: css`
            border: 0;
            padding: 0;
        `,
    },
    carouselGridItem: { width: 10 },
    verticalCarouselGridItem: { width: 12 },
    carouselWrapper: {
        css: css`
            width: 100%;
        `,
    },
    carouselContainer: {
        css: css`
            overflow: hidden;
        `,
    },
    carouselSlideInner: {
        css: css`
            width: 100%;
            text-align: center;
            position: relative;
            padding: 10px 3px 0;
            display: flex;
            justify-content: center;
        `,
    },
    image: {
        css: getImageCss("common.border"),
    },
    selectedImage: {
        css: getImageCss("text.main"),
    },
    nextArrowGridItem: {
        width: 1,
        align: "middle",
        css: css`
            justify-content: flex-start;
        `,
    },
    verticalNextArrowGridItem: {
        width: 12,
        align: "middle",
        css: css`
            justify-content: center;
        `,
    },
    nextArrowButton: {
        variant: "secondary",
        color: "text.main",
        css: css`
            border: 0;
            padding: 0;
        `,
    },
};

export const CarouselSlidesContainer = styled.div<
    InjectableCss & { isVertical: boolean; numberOfVisibleThumbnails: number }
>`
    display: flex;
    flex-direction: ${props => (props.isVertical ? "column" : "row")};
    align-items: ${props => (props.isVertical ? "center" : "initial")};
    ${props => (props.isVertical ? `height: ${props.numberOfVisibleThumbnails * 74}px;` : "")}
    ${injectCss};
`;

export const CarouselSlide = styled.div<InjectableCss & { numberOfVisibleThumbnails: number }>`
    flex: 0 0 ${props => 100 / props.numberOfVisibleThumbnails}%;
    padding: 0;
    position: relative;
    ${injectCss};
`;

const ProductImageCarousel = ({
    productSettings,
    productContext,
    selectedIndex,
    onSelectImage,
    extendedStyles,
    scrollingBehavior,
    numberOfVisibleThumbnails = 6,
    // XNG-Change
    videos,
    onSelectVideo,
    selectedVideoIndex,
    isTabletScreen,
}: Props) => {
    const [styles] = useState(() => mergeToNew(productImageCarouselStyles, extendedStyles));
    // XNG-Change add isLoading
    const { product, isLoading } = productContext as XProductContextModel;
    const enableCarousel = (product?.images ? product.images.length : 0) + (videos ? videos.length : 0) > 0;

    const [selectedImageIndex, setSelectedImageIndex] = useState(selectedIndex);

    useEffect(() => {
        setSelectedImageIndex(selectedIndex);
    }, [selectedIndex]);

    const imageClickHandler = (index: number) => {
        setSelectedImageIndex(index);
        onSelectImage?.(index);
        // Clear video selection when image is selected
        onSelectVideo?.(-1);
    };

    const videoClickHandler = (index: number) => {
        // Clear image selection when video is selected
        setSelectedImageIndex(-1);
        onSelectVideo?.(index);
    };

    // const isVertical = scrollingBehavior === "vertical";
    // const [emblaRef, embla] = useEmblaCarousel({
    //     axis: isVertical ? "y" : "x",
    // });
    // const [canScrollPrev, setCanScrollPrev] = useState(false);
    // const [canScrollNext, setCanScrollNext] = useState(false);

    // const setCanScroll = () => {
    //     setCanScrollPrev(!!embla && embla.canScrollPrev());
    //     setCanScrollNext(!!embla && embla.canScrollNext());
    // };
    //
    //     const updateVisibility = () => {
    //         if (!embla || !product.images) {
    //             return;
    //         }
    //
    //         const canScroll = embla.canScrollPrev() || embla.canScrollNext();
    //         if (!canScroll) {
    //             embla.slideNodes().forEach(slide => {
    //                 slide.setAttribute("data-is-visible", "true");
    //             });
    //             return;
    //         }
    //
    //         const viewport = embla.rootNode().getBoundingClientRect();
    //         const buffer = 5;
    //
    //         embla.slideNodes().forEach((slide, index) => {
    //             const slideRect = slide.getBoundingClientRect();
    //             const isFullyVisible =
    //                 slideRect.top >= viewport.top - buffer &&
    //                 slideRect.bottom <= viewport.bottom + buffer &&
    //                 slideRect.left >= viewport.left - buffer &&
    //                 slideRect.right <= viewport.right + buffer;
    //
    //             slide.setAttribute("data-is-visible", isFullyVisible ? "true" : "false");
    //         });
    //     };

    // useEffect(() => {
    //     if (!embla) {
    //         return;
    //     }

    //     const handleUpdate = () => {
    //         setCanScroll();
    //         updateVisibility();

    //     };

    //     embla.on("init", handleUpdate);
    //     embla.on("select", handleUpdate);
    //     embla.on("reInit", handleUpdate);
    //     embla.on("settle", handleUpdate);

    //     handleUpdate();
    // }, [embla]);

    // useEffect(() => {
    //     if (!embla || !product.images) {
    //         return;
    //     }

    //     embla.reInit({
    //         align: product.images.length > numberOfVisibleThumbnails || isVertical ? "start" : "center",
    //         slidesToScroll: numberOfVisibleThumbnails,
    //         axis: isVertical ? "y" : "x",
    //     });

    //     updateVisibility();
    // }, [embla, product, scrollingBehavior, numberOfVisibleThumbnails]);

    const isVertical = scrollingBehavior === "vertical";

    const getProductImageThumbPath = (productImage: ImageModel) => {
        if (productImage.imageType === "Static") {
            return productImage.smallImagePath || productImage.mediumImagePath;
        }
        if (productImage.imageType === "360" && productSettings.imageProvider === "SIRV") {
            return productImage.mediumImagePath + (productImage.mediumImagePath.includes("?") ? "&thumb" : "?thumb");
        }
        return "";
    };

    // XNG-Change: add skeleton
    if (isLoading) {
        return (
            <StyledWrapper {...styles.loadingWrapper} {...{ isVertical }}>
                {Array.from({ length: numberOfVisibleThumbnails }).map((_, index) => (
                    <StyledWrapper {...styles.carouselSlideInner} key={index}>
                        <Skeleton height="90px" width="90px" />
                    </StyledWrapper>
                ))}
            </StyledWrapper>
        );
    }

    if (!product || !enableCarousel) {
        return null;
    }

    return (
        <StyledWrapper {...styles.carouselWrapper} key={product.id}>
            {/* <GridItem {...(isVertical ? styles.verticalPrevArrowGridItem : styles.prevArrowGridItem)}>
                {product.images.length > numberOfVisibleThumbnails && (
                    <Button
                        {...styles.prevArrowButton}
                        onClick={() => embla && embla.scrollPrev()}
                        disabled={!canScrollPrev}
                        aria-label={translate("Previous")}
                        data-test-selector="prevBtn"
                    >
                        <ButtonIcon src={isVertical ? ChevronUp : ChevronLeft} />
                    </Button>
                )}
            </GridItem>
            <GridItem {...(isVertical ? styles.verticalCarouselGridItem : styles.carouselGridItem)}>
                <StyledWrapper {...styles.carouselWrapper}>
                    <StyledWrapper {...styles.carouselContainer} ref={emblaRef}>
                        <CarouselSlidesContainer
                            isVertical={isVertical}
                            numberOfVisibleThumbnails={numberOfVisibleThumbnails}
                            data-test-selector="slides"
                            data-test-selector-isVertical={isVertical}
                        >
                            {product.images.map((productImage, index) => (
                                <CarouselSlide
                                    key={productImage.id}
                                    numberOfVisibleThumbnails={numberOfVisibleThumbnails}
                                       data-is-selected={index === selectedImageIndex}
                                    data-is-visible="false"
                                >
                                    <StyledWrapper {...styles.carouselSlideInner} data-test-selector="carousel_slide">
                                        <Clickable onClick={() => imageClickHandler(index)}>
                                            <LazyImage
                                                {...(index === selectedImageIndex
                                                    ? styles.selectedImage
                                                    : styles.image)}
                                                src={getProductImageThumbPath(productImage)}
                                                altText={productImage.imageAltText}
                                            />
                                        </Clickable>
                                    </StyledWrapper>
                                </CarouselSlide>
                            ))}
                        </CarouselSlidesContainer>
                    </StyledWrapper>
                </StyledWrapper>
            </GridItem>
            <GridItem {...(isVertical ? styles.verticalNextArrowGridItem : styles.nextArrowGridItem)}>
                {product.images.length > numberOfVisibleThumbnails && (
                    <Button
                        {...styles.nextArrowButton}
                        onClick={() => embla && embla.scrollNext()}
                        disabled={!canScrollNext}
                        aria-label={translate("Next")}
                        data-test-selector="nextBtn"
                    >
                        <ButtonIcon src={isVertical ? ChevronDown : ChevronRight} />
                    </Button>
                )}
            </GridItem> */}
            <XngCarousel
                scrollbar={false}
                navigation={isVertical}
                showArrowsOnMobile={false}
                draggable
                slidesPerView="auto"
                extendedStyles={styles.carousel}
                spaceBetween={16}
                slideWidth="91px"
                direction={scrollingBehavior}
                withShadow={false}
                centerInsufficientSlides={false}
                centeredSlides={false}
                hideArrowIfDisabled={true}
                key={isVertical ? "vertical carousel" : "horizontal carousel"}
                height={isVertical ? 543 : undefined}
            >
                {product?.images?.map((productImage, index) => (
                    <StyledWrapper
                        {...styles.carouselSlideInner}
                        data-test-selector="carousel_slide"
                        key={`slide_${index}`}
                        data-is-selected={index === selectedImageIndex && selectedVideoIndex === -1}
                        data-is-visible="false"
                    >
                        <Clickable
                            onClick={() => imageClickHandler(index)}
                            // XNG-Change
                            aria-label={translate("product image clickable")}
                        >
                            <LazyImage
                                {...(index === selectedImageIndex && selectedVideoIndex === -1
                                    ? styles.selectedImage
                                    : styles.image)}
                                src={getProductImageThumbPath(productImage)}
                                altText={productImage.imageAltText}
                            />
                        </Clickable>
                    </StyledWrapper>
                ))}
                {videos?.map((video: VideoDocumentModel, index) => (
                    <StyledWrapper
                        {...styles.carouselSlideInner}
                        data-test-selector="carousel_slide"
                        key={`slide_${index}`}
                        data-is-selected={index === selectedVideoIndex}
                        data-is-visible="false"
                    >
                        <Clickable
                            onClick={() => videoClickHandler(index)}
                            // XNG-Change
                            aria-label={translate("product video clickable")}
                        >
                            <LazyImage
                                {...(index === selectedVideoIndex ? styles.selectedImage : styles.image)}
                                src={video.thumbnailPath}
                                altText={video.description}
                            />
                        </Clickable>
                        <StyledWrapper {...styles.videoIcon}>
                            <VideoPlayButton size={32} />
                        </StyledWrapper>
                    </StyledWrapper>
                ))}
            </XngCarousel>
        </StyledWrapper>
    );
};

export default withProductContext(connect(mapStateToProps)(ProductImageCarousel));
